import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

const Header: React.FC = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  // 메뉴 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    };

    if (isMenuOpen || isProfileMenuOpen) {
      // click 이벤트 사용 — mousedown/touchstart는 iOS에서
      // 버튼 onClick보다 먼저 발동하여 메뉴 네비게이션이 씹히는 문제 유발
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMenuOpen, isProfileMenuOpen]);

  const handleNavigation = (path: string) => {
    setIsMenuOpen(false);
    if (location.pathname === path) {
      // 같은 페이지면 강제 새로고침
      window.location.reload();
    } else {
      navigate(path);
    }
  };

  return (
    <header
      className="bg-primary text-white py-4 shadow-lg sticky top-0 z-50"
      style={{ paddingTop: 'calc(1rem + env(safe-area-inset-top))' }}
    >
      <div className="container mx-auto px-4">
        {/* 상단 바: 홈 로고 + 타이틀 + 메뉴 버튼 */}
        <div className="flex items-center justify-between mb-2">
          {/* 좌측: 돈까스 아조씨 로고 + 타이틀 */}
          <div className="flex items-center space-x-2">
            <Link to="/" className="hover:scale-110 transition-transform">
              <img 
                src="/images/azocsii-logo.png" 
                alt="AZOCSII 로고"
                className="w-8 h-8 md:w-10 md:h-10 object-contain"
              />
            </Link>
            <Link to="/" className="hover:opacity-80 transition-opacity">
              <div>
                <h1 className="text-lg md:text-2xl font-bold">
                  <span className="text-red-500">A</span>
                  <span className="text-orange-500">Z</span>
                  <span className="text-yellow-400">O</span>
                  <span className="text-green-500">C</span>
                  <span className="text-blue-500">S</span>
                  <span className="text-indigo-500">I</span>
                  <span className="text-purple-500">I</span>
                </h1>
                <p className="text-xs text-gray-300 hidden md:block">
                  상담사 권리 보호 플랫폼
                </p>
              </div>
            </Link>
          </div>

          {/* 우측: 메뉴 버튼 */}
          <div className="flex items-center space-x-1">
            {/*
              다크모드 토글 (Phase 0-A 일시 중단, 출시 후 리브랜딩 시 복구 예정)
              Step 2c에서 추가, 2026-04-15 회의감 피드백으로 숨김.
              인프라(ThemeContext, CSS 변수)는 유지됨.

              <button
                onClick={toggleTheme}
                aria-label={theme === 'dark' ? '라이트 모드로 전환' : '다크 모드로 전환'}
                className="flex items-center justify-center text-xl md:text-2xl hover:bg-white/10 px-2 md:px-3 py-2 rounded-lg transition-all"
              >
                {theme === 'dark' ? '☀️' : '🌙'}
              </button>
            */}
          <div ref={menuRef}>
            <button
              ref={menuBtnRef}
              onClick={() => {
                if (!isMenuOpen && menuBtnRef.current) {
                  const rect = menuBtnRef.current.getBoundingClientRect();
                  setMenuPos({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
                }
                setIsMenuOpen(!isMenuOpen);
              }}
              className="flex items-center space-x-1 text-base md:text-lg hover:bg-white/10 px-2 md:px-3 py-2 rounded-lg transition-all"
            >
              <span className="text-xs md:text-sm font-medium">메뉴</span>
              <span className="text-xl md:text-2xl">☰</span>
            </button>

            {/* 펼쳐지는 메뉴 — fixed로 stacking context 탈출, 콘텐츠 영역 위에 배치 */}
            {isMenuOpen && (
              <div
                className="fixed z-[9999] bg-gradient-to-br from-purple-500/90 via-pink-500/90 to-orange-500/90 backdrop-blur-xl rounded-2xl shadow-2xl p-3 space-y-1.5 min-w-[140px] border border-white/20"
                style={{ top: menuPos.top, right: menuPos.right }}
              >
                {/* 로그인 (비로그인 시 최상단) */}
                {!isAuthenticated && (
                  <>
                    <div className="rainbow-border">
                      <Link
                        to="/login"
                        onClick={() => setIsMenuOpen(false)}
                        className="rainbow-border-inner flex items-center space-x-2 px-3 py-2 hover:bg-white/30 rounded-xl transition-all text-white font-bold text-sm"
                      >
                        <span className="text-lg">🔐</span>
                        <span>로그인</span>
                      </Link>
                    </div>
                    <div className="border-t border-white/30 my-1.5"></div>
                  </>
                )}
                
                {/* 메인 메뉴 3개 - 터치 영역 확대 */}
                <button
                  onClick={() => handleNavigation('/scheduler')}
                  className="w-full flex items-center space-x-2 px-3 py-3 bg-white/10 hover:bg-white/20 rounded-xl transition-all text-white font-medium text-sm backdrop-blur-sm touch-manipulation active:scale-95"
                >
                  <span className="text-lg">📅</span>
                  <span>스케줄러</span>
                </button>
                <button
                  onClick={() => handleNavigation('/lotto')}
                  className="w-full flex items-center space-x-2 px-3 py-3 bg-white/10 hover:bg-white/20 rounded-xl transition-all text-white font-medium text-sm backdrop-blur-sm touch-manipulation active:scale-95"
                >
                  <span className="text-lg">🎲</span>
                  <span>로또</span>
                </button>
                <button
                  onClick={() => handleNavigation('/swap')}
                  className="w-full flex items-center space-x-2 px-3 py-3 bg-white/10 hover:bg-white/20 rounded-xl transition-all text-white font-medium text-sm backdrop-blur-sm touch-manipulation active:scale-95"
                >
                  <span className="text-lg">🤝</span>
                  <span>스왑</span>
                </button>

                {/* 관리자 전용 메뉴 */}
                {(user?.role === 'TEAM_LEADER' || user?.role === 'COMPANY_ADMIN') && (
                  <>
                    <div className="border-t border-white/30 my-1.5"></div>
                    <button
                      onClick={() => handleNavigation('/admin')}
                      className="w-full flex items-center space-x-2 px-3 py-2 bg-yellow-500/90 hover:bg-yellow-600/90 rounded-xl transition-all text-yellow-900 font-bold text-sm backdrop-blur-sm border border-yellow-600"
                    >
                      <span className="text-lg">👑</span>
                      <span>관리자</span>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
          </div>
        </div>

        {/* 하단: 닉네임 (좌측 끝, 클릭 가능) */}
        {isAuthenticated && (
          <div className="flex items-center space-x-2 pl-1 relative" ref={profileMenuRef}>
            <button
              onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
              className="flex items-center space-x-2 hover:bg-white/10 px-3 py-2 rounded-lg transition-all"
            >
              {(user?.role === 'TEAM_LEADER' || user?.role === 'COMPANY_ADMIN') && (
                <span className="text-xs px-1.5 py-0.5 bg-yellow-500 text-yellow-900 rounded font-bold">
                  {user?.role === 'COMPANY_ADMIN' ? '👑' : '⭐'}
                </span>
              )}
              <span className="text-sm font-medium text-white">
                {user?.name}님
              </span>
              <span className="text-xs text-white/60">▼</span>
            </button>

            {/* 프로필 드롭다운 메뉴 */}
            {isProfileMenuOpen && (
              <div className="absolute left-0 top-full mt-2 bg-white rounded-xl shadow-2xl p-2 space-y-1 min-w-[160px] z-50">
                <Link
                  to="/profile"
                  onClick={() => setIsProfileMenuOpen(false)}
                  className="flex items-center space-x-3 px-4 py-3 hover:bg-gray-100 rounded-lg transition-all text-gray-800"
                >
                  <span className="text-lg">👤</span>
                  <span className="font-medium">내 정보</span>
                </Link>

                {/* 관리자 전용 메뉴 */}
                {(user?.role === 'TEAM_LEADER' || user?.role === 'COMPANY_ADMIN') && (
                  <Link
                    to="/admin/schedule"
                    onClick={() => setIsProfileMenuOpen(false)}
                    className="flex items-center space-x-3 px-4 py-3 hover:bg-orange-50 rounded-lg transition-all text-orange-600"
                  >
                    <span className="text-lg">📋</span>
                    <span className="font-medium">휴무 승인</span>
                  </Link>
                )}
                
                <button
                  onClick={() => {
                    logout();
                    setIsProfileMenuOpen(false);
                  }}
                  className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-red-50 rounded-lg transition-all text-red-600"
                >
                  <span className="text-lg">🚪</span>
                  <span className="font-medium">로그아웃</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
